/**
 * @file Enemies leave their gun behind (GDD §Fase 3 — weapon swap).
 *
 * Separate from {@link ./PickupSystem.js} on purpose: that system rolls a random
 * drop table, this one drops a specific, already-used object. The two are
 * independent, so a spitter can leave both a medkit and its spitgun.
 */

import { System } from '../ecs/System.js';
import { AIBrain, RangedAI, Transform, Weapon, Renderable, Pickup, InteractionTarget } from '../components/index.js';
import { Collider } from '../components/Collider.js';
import { pickupDefinition } from '../config/Powerups.js';
import { Events } from '../core/EventBus.js';
import { Prefab } from '../rendering/Prefabs.js';
import { DROPS } from '../config/Powerups.js';
import { Layer } from '../config/Layers.js';
import { Lifetime } from '../components/Lifetime.js';

export class WeaponDropSystem extends System {
  /** Runs after `PickupSystem` (55) so the two drops never race for the spot. */
  static priority = 56;

  constructor() {
    super();
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
  }

  /** @returns {void} */
  init() {
    this._subscriptions.push(
      this.ctx.events.on(Events.DIED, (payload) => this._dropWeapon(payload))
    );
  }

  /**
   * @param {{ entity: import('../ecs/Entity.js').Entity }} payload Death event.
   * @returns {void}
   * @private
   */
  _dropWeapon({ entity }) {
    // Only enemies: the player dying ends the run, and a corpse-side gun would
    // just litter the game-over screen. Both AI kinds count — melee/ranged foes
    // run on AIBrain, while the ranged-template roster (Sentinel and the issue
    // #57 armed variants) runs on RangedAI — so every armed enemy drops its gun.
    if (!entity.has(AIBrain) && !entity.has(RangedAI)) return;

    const weapon = entity.get(Weapon);
    const transform = entity.get(Transform);
    if (!weapon || !transform) return;

    // A weapon with no pickup definition cannot be drawn or picked up; dropping
    // it would leave an invisible entity lying around.
    if (!pickupDefinition(weapon.key)) return;

    // The very stat block the enemy was firing, not a fresh copy of it.
    weapon.triggerHeld = false;

    // Bug 1 fix: create a floor pickup that shows the actual weapon model (not a
    // generic octahedro) and carries the enemy's exact stat block so the player
    // picks up the real weapon (Bug 2: preserve the weapon instance, don't rebuild).
    const { meshFactory } = this.ctx;
    const renderableKey = Prefab.weapon(weapon.key);
    const renderable = new Renderable(renderableKey, meshFactory.acquire(renderableKey), { yOffset: 0 });

    // Build the pickup entity manually so we control every component.
    const pickupEntity = this.ctx.world.createEntity('pickup');
    const definition = pickupDefinition(weapon.key);
    if (!definition) return;

    pickupEntity.add(new Transform(transform.position.x, DROPS.hoverHeight, transform.position.z));
    pickupEntity.add(new Pickup(weapon.key, definition, weapon));
    pickupEntity.add(new Lifetime(DROPS.weaponLifetime));
    pickupEntity.add(new InteractionTarget(definition.name));
    pickupEntity.add(new Collider({
      halfX: DROPS.pickupRadius,
      halfZ: DROPS.pickupRadius,
      layer: Layer.PICKUP,
      mask: Layer.PLAYER,
      isTrigger: true
    }));
    // Weapon model instead of octahedro.
    pickupEntity.add(renderable);
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
  }
}